import { type ReactNode } from "react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@strivelabs/ui-core";
import { useModeFraction } from "./context";
import {
  COMPOSER_MODE_ACCENT_RGB,
  COMPOSER_MODE_EDGE_GRADIENT,
} from "./tones";

export interface ComposerSurfaceProps {
  className?: string;
  children: ReactNode;
}

/** Mask that keeps only the outer band of the edge layer, cutting its centre. */
const EDGE_BAND_MASK =
  "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)";

/**
 * The composer writing surface: a bordered card (shadcn tokens). On hover and
 * focus-within it darkens its gray border and ring (the inner and outer edges)
 * and deepens the shadow so it reads as active while typing. The active
 * response mode fades a warm gradient into the whole edge band between the
 * border and the ring, and a soft orange halo breathes in and out around it, so
 * Deep Dive reads as the more powerful mode.
 */
export const ComposerSurface = ({
  className,
  children,
}: ComposerSurfaceProps) => {
  const fraction = useModeFraction();
  const reduceMotion = useReducedMotion();

  return (
    <div
      className={cn(
        "relative rounded-[22px] border border-border bg-card shadow-[0_1px_2px_-1px_rgba(0,0,0,0.06),0_2px_10px_-6px_rgba(0,0,0,0.08)]",
        "ring-1 ring-border/70 ring-offset-2 ring-offset-background",
        "transition-[border-color,box-shadow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
        "hover:border-muted-foreground/25 hover:ring-muted-foreground/15 hover:shadow-[0_2px_5px_-2px_rgba(0,0,0,0.08),0_6px_18px_-8px_rgba(0,0,0,0.10)]",
        "focus-within:border-muted-foreground/40 focus-within:ring-muted-foreground/25 focus-within:shadow-[0_3px_8px_-3px_rgba(0,0,0,0.08),0_10px_24px_-8px_rgba(0,0,0,0.11)]",
        className,
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -inset-[2px] rounded-[24px] transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
        style={{ opacity: fraction }}
      >
        <div
          className="absolute inset-0 rounded-[inherit]"
          style={{
            padding: "1.5px",
            background: COMPOSER_MODE_EDGE_GRADIENT,
            WebkitMask: EDGE_BAND_MASK,
            WebkitMaskComposite: "xor",
            mask: EDGE_BAND_MASK,
            maskComposite: "exclude",
          }}
        />
        <motion.div
          className="absolute inset-0 rounded-[inherit]"
          style={{
            boxShadow: `0 0 9px rgba(${COMPOSER_MODE_ACCENT_RGB},0.32), 0 0 22px rgba(${COMPOSER_MODE_ACCENT_RGB},0.2)`,
          }}
          animate={
            reduceMotion || fraction <= 0 ? undefined : { opacity: [0.5, 0.85, 0.5] }
          }
          transition={
            reduceMotion || fraction <= 0
              ? undefined
              : { repeat: Number.POSITIVE_INFINITY, duration: 5, ease: "easeInOut" }
          }
        />
      </div>
      {children}
    </div>
  );
};
